import { useCallback, useEffect, useMemo, useRef } from "react";
import { Unity, useUnityContext, useUnityMetricsInfo } from "react-unity-webgl";
import UnityFpsPanel from "./UnityFpsPanel";
import { useSpatial } from "./SpatialContext";
import { useBookshelf } from "./BookshelfContext";
import supabase from "../services/supabaseClient";
import {
  parseUnityOutgoingMessage,
  pushUnityBridgeSession,
  pushWorldUnlockStates,
  pushEmergencyClearWorld,
  isReactBridgeMissingError,
  worldStructureKey,
  diffWorldPatches,
  explainWorldDiff,
  buildUnityWorld,
  pushWorldPatches,
} from "../lib/unityBridge";
import {
  dispatchUnityBridgeEvent,
  isUnityInBuildingSession,
  isUnityWorldBuilt,
  markUnityWorldLoading,
  setUnityPatchFailedHandler,
  setUnityPendingWorldSyncHandler,
} from "../lib/unityEvents";
import { registerUnitySendMessage } from "../lib/unityCommandBridge";
import { markBooksSynced } from "../lib/bookshelf";
import { installUnityKeyboardGuard } from "../lib/unityKeyboardGuard";

const UNITY_BUILD_Q = "?v="+process.env.REACT_APP_UNITY_BUILD_VERSION;

function unityAssetBaseUrl() {
  return `${window.location.origin}/unity-netlify`;
}

const DEFAULT_UNITY_EVENT = "OnUnityMessage";
const UNITY_EVENT_FALLBACKS = ["OnSendEvent"];
const UNITY_WINDOW_EVENT_FALLBACKS = ["unity-message", "OnUnityMessage", "OnSendEvent"];
const UNITY_KNOWN_BRIDGE_EVENTS = [
  "BridgeReady",
  "HotspotClicked",
  "HotspotClosed",
  "RoomEntered",
  "BuildingEntered",
  "BuildingExited",
  "DoorClicked",
  "BookClicked",
  "BookAdded",
  "BookAddFailed",
  "BooksReordered",
  "WorldReady",
  "SessionRestored",
  "SessionState",
  "LayoutCorrected",
  "PatchFailed",
  "IslandRemoved",
  "BuildingColorsAssigned",
];

function tryParseJson(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function unwrapUnityPayload(payload) {
  let current = payload;
  for (let i = 0; i < 4; i += 1) {
    if (typeof current === "string") {
      const parsed = tryParseJson(current);
      if (parsed === current) break;
      current = parsed;
      continue;
    }
    if (!current || typeof current !== "object" || Array.isArray(current)) break;
    if (Object.prototype.hasOwnProperty.call(current, "jsonData")) {
      current = current.jsonData;
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(current, "detail")) {
      current = current.detail;
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(current, "data")) {
      current = current.data;
      continue;
    }
    if (Object.prototype.hasOwnProperty.call(current, "message")) {
      current = current.message;
      continue;
    }
    break;
  }
  return current;
}

function logUnityInbound(message, channelLabel) {
  if (process.env.NODE_ENV !== "development") return;
  console.debug(`[Unity → React] ${channelLabel}`, unwrapUnityPayload(message));
}

export default function UnityMessagePanel({ fullscreen = false, onLoadChange, holdReveal = false }) {
  const { user, appUser, userProfile, world, worldReady, getUnityWorldPayload, needsBrainDump } = useSpatial();

  const unityBase = unityAssetBaseUrl();
  const BUILD_NAME = process.env.REACT_APP_UNITY_BUILD_NAME;
  const unityConfig = useMemo(
    () => ({
      loaderUrl: `${unityBase}/Build/${BUILD_NAME}.loader.js${UNITY_BUILD_Q}`,
      dataUrl: `${unityBase}/Build/${BUILD_NAME}.data.unityweb${UNITY_BUILD_Q}`,
      frameworkUrl: `${unityBase}/Build/${BUILD_NAME}.framework.js.unityweb${UNITY_BUILD_Q}`,
      codeUrl: `${unityBase}/Build/${BUILD_NAME}.wasm.unityweb${UNITY_BUILD_Q}`,
      streamingAssetsUrl: `${unityBase}/StreamingAssets`,
      companyName: "DefaultCompany",
      productName: "GoNeuroQuest",
      productVersion: "0.1.0",
    }),
    [unityBase]
  );
  const {
    unityProvider,
    sendMessage,
    addEventListener,
    removeEventListener,
    isLoaded,
    loadingProgression,
    initialisationError,
    getMetricsInfo,
  } = useUnityContext(unityConfig);
  const { fps } = useUnityMetricsInfo(getMetricsInfo, { interval: 1000 });

  const { documents: bookshelfDocs, loaded: bookshelfLoaded } = useBookshelf();

  const worldHierarchySyncKey = useMemo(
    () => worldStructureKey({ id: world?.id, name: world?.name, districts: world?.districts }),
    [world?.id, world?.name, world?.districts]
  );

  /** Avoid SyncWorldState when only auth/profile changes — reloading the city mid-inspection breaks room entry. */
  const lastSyncedWorldKeyRef = useRef(null);
  const lastSyncedDistrictsRef = useRef(null);
  const prevNeedsBrainDumpRef = useRef(null);
  const syncInFlightRef = useRef(false);
  const sceneReadyNotifiedRef = useRef(false);

  const notifySceneReady = useCallback(() => {
    if (sceneReadyNotifiedRef.current) return;
    sceneReadyNotifiedRef.current = true;
    // Allow Unity to paint the Haven scene after SyncWorldState before showing Lumen.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        onLoadChange?.(true);
      });
    });
  }, [onLoadChange]);

  const syncBridgeToUnity = useCallback(async () => {
    if (!isLoaded || !worldReady) return null;
    if (syncInFlightRef.current) return null;

    const { data: { session } } = await supabase.auth.getSession();
    if (!user && !session?.user) {
      console.warn("[NeuroQuest → Unity] bridge sync skipped — not signed in");
      return null;
    }

    const authUser = user ?? session?.user;
    const payload = getUnityWorldPayload?.() ?? { districts: [], surface_items: [] };

    let shouldSyncWorld = lastSyncedWorldKeyRef.current !== worldHierarchySyncKey;

    const worldPatches = shouldSyncWorld && isUnityWorldBuilt()
      ? diffWorldPatches(lastSyncedDistrictsRef.current, payload.districts)
      : null;
    if (shouldSyncWorld && !worldPatches) {
      console.info(
        "[NeuroQuest → Unity] full SyncWorldState because:",
        isUnityWorldBuilt()
          ? explainWorldDiff(lastSyncedDistrictsRef.current, payload.districts)
          : "Unity hasn't finished building the last world (no WorldReady yet)"
      );
    }
    if (worldPatches) {
      syncInFlightRef.current = true;
      try {
        const unityWorld = await buildUnityWorld(
          { id: payload.id, name: payload.name, districts: payload.districts },
          payload.surface_items,
          bookshelfLoaded ? bookshelfDocs : undefined
        );
        const patched = await pushWorldPatches(sendMessage, worldPatches, unityWorld);
        if (patched.ok) {
          lastSyncedWorldKeyRef.current = worldHierarchySyncKey;
          lastSyncedDistrictsRef.current = payload.districts;
          console.info("[NeuroQuest → Unity] ApplyWorldPatch delivered", worldPatches.map((p) => p.op));
          return { ok: true, worldSynced: true, patched: true };
        }
        console.warn("[NeuroQuest → Unity] world patch failed — falling back to SyncWorldState", patched.error);
      } finally {
        syncInFlightRef.current = false;
      }
    }

    if (shouldSyncWorld && isUnityInBuildingSession()) {
      shouldSyncWorld = false;
      console.info("[NeuroQuest → Unity] deferring world sync — building session active");
      return { ok: true, worldSynced: false, deferred: true };
    }

    syncInFlightRef.current = true;
    try {
      const result = await pushUnityBridgeSession({
        sendMessage,
        user: authUser,
        session,
        appUser,
        userProfile,
        hierarchy: { id: payload.id, name: payload.name, districts: payload.districts },
        surfaceItems: payload.surface_items,
        bookshelfDocs: bookshelfLoaded ? bookshelfDocs : undefined,
        syncWorld: shouldSyncWorld,
        isNewUser: needsBrainDump,
      });
      if (result.worldSynced) {
        lastSyncedWorldKeyRef.current = worldHierarchySyncKey;
        if (shouldSyncWorld) lastSyncedDistrictsRef.current = payload.districts;
        if (bookshelfLoaded) {
          markBooksSynced(
            bookshelfDocs.filter((d) => d.status !== "archived").map((d) => d.id)
          );
        }
        await pushWorldUnlockStates(sendMessage, result.districts);
        const method = result.districts?.length ? "SyncWorldState" : `ClearWorld(${needsBrainDump ? "true" : "false"})`;
        console.info(`[NeuroQuest → Unity] ${method} delivered`, {
          rooms: result.worldRoomCount,
          surfaces: result.surfaceItemCount,
        });
        notifySceneReady();
      } else if (!result.ok && shouldSyncWorld) {
        const bridgeMissing = result.failures?.some((f) => isReactBridgeMissingError(f.error));
        console.warn("[NeuroQuest → Unity] bridge sync incomplete — will retry", {
          bridgeMissing,
          failures: result.failures?.map((f) => f.method),
          shouldSyncWorld,
        });
      }
      return result;
    } catch (e) {
      console.warn("[NeuroQuest → Unity] bridge sync failed", e);
      return null;
    } finally {
      syncInFlightRef.current = false;
    }
  }, [
    isLoaded,
    worldReady,
    sendMessage,
    user,
    appUser,
    userProfile,
    getUnityWorldPayload,
    worldHierarchySyncKey,
    needsBrainDump,
    notifySceneReady,
    bookshelfDocs,
    bookshelfLoaded,
  ]);

  useEffect(() => {
    if (!isLoaded || !worldReady) {
      sceneReadyNotifiedRef.current = false;
      onLoadChange?.(false);
    }
  }, [isLoaded, worldReady, onLoadChange]);

  useEffect(() => {
    if (!isLoaded || !worldReady) return;
    if (lastSyncedWorldKeyRef.current === worldHierarchySyncKey) {
      notifySceneReady();
    }
  }, [isLoaded, worldReady, worldHierarchySyncKey, notifySceneReady]);

  useEffect(() => {
    const preventContextMenuOpen = (e)=>{ e.preventDefault(); };
    const canvas = document.querySelector("canvas");
    if(canvas){
      canvas.addEventListener("contextmenu",preventContextMenuOpen);
    }
    return () => { 
      if(canvas)canvas.removeEventListener("contextmenu",preventContextMenuOpen)
    };
  },[isLoaded]);

  const syncBridgeRef = useRef(syncBridgeToUnity);
  syncBridgeRef.current = syncBridgeToUnity;

  const noteUnityBridgeReady = useCallback(() => {
    if (lastSyncedWorldKeyRef.current === worldHierarchySyncKey) return;
    void syncBridgeRef.current();
  }, [worldHierarchySyncKey]);

  useEffect(() => {
    if (!isLoaded) {
      lastSyncedWorldKeyRef.current = null;
      lastSyncedDistrictsRef.current = null;
      markUnityWorldLoading();
    }
  }, [isLoaded]);

  useEffect(() => {
    setUnityPatchFailedHandler(() => {
      lastSyncedWorldKeyRef.current = null;
      lastSyncedDistrictsRef.current = null;
      void syncBridgeRef.current();
    });
    return () => setUnityPatchFailedHandler(null);
  }, []);

  // Keep pushing SetAuthData → SetUserProfile → SyncWorldState until Unity accepts the world JSON.
  useEffect(() => {
    if (!isLoaded || !worldReady || !user?.id) return undefined;

    let cancelled = false;
    let retryTimer = null;
    let attempts = 0;
    const maxAttempts = 30;
    const retryMs = 2000;
    const initialDelayMs = 3500;

    const run = async () => {
      if (cancelled) return;
      if (lastSyncedWorldKeyRef.current === worldHierarchySyncKey) return;

      attempts += 1;
      const result = await syncBridgeRef.current();
      if (cancelled) return;
      if (result?.worldSynced) return;
      if (result?.deferred) return;

      if (attempts < maxAttempts) {
        retryTimer = setTimeout(run, retryMs);
      } else {
        console.warn("[NeuroQuest → Unity] SyncWorldState not confirmed after max retries", {
          attempts,
        });
        if (!cancelled) void pushEmergencyClearWorld(sendMessage, needsBrainDump);
        notifySceneReady();
      }
    };

    const startTimer = setTimeout(run, initialDelayMs);
    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isLoaded, worldReady, user?.id, userProfile?.user_id, worldHierarchySyncKey, notifySceneReady, sendMessage, needsBrainDump]);

  // After Brain Dump completes (needsBrainDump true → false), queue a full world resync.
  useEffect(() => {
    if (prevNeedsBrainDumpRef.current === null) {
      prevNeedsBrainDumpRef.current = needsBrainDump;
      return undefined;
    }
    const wasNeeding = prevNeedsBrainDumpRef.current;
    prevNeedsBrainDumpRef.current = needsBrainDump;

    if (!isLoaded || needsBrainDump || !wasNeeding) return undefined;

    lastSyncedWorldKeyRef.current = null;
    const timer = setTimeout(() => {
      void syncBridgeToUnity();
    }, 400);
    return () => clearTimeout(timer);
  }, [isLoaded, needsBrainDump, syncBridgeToUnity]);

  useEffect(() => {
    setUnityPendingWorldSyncHandler((isClearWorld = false) => {
      void syncBridgeToUnity(isClearWorld);
    });
    return () => setUnityPendingWorldSyncHandler(null);
  }, [syncBridgeToUnity]);

  useEffect(() => {
    if (isLoaded && sendMessage) {
      registerUnitySendMessage(sendMessage);
    } else {
      registerUnitySendMessage(null);
    }
    return () => registerUnitySendMessage(null);
  }, [isLoaded, sendMessage]);

  useEffect(() => {
    const unityEventName = process.env.REACT_APP_UNITY_RESPONSE_EVENT || DEFAULT_UNITY_EVENT;
    const unityEventNames = [unityEventName, ...UNITY_EVENT_FALLBACKS, ...UNITY_KNOWN_BRIDGE_EVENTS].filter(
      (name, index, arr) => name && arr.indexOf(name) === index
    );
    const onUnityResponse = (eventName) => (...args) => {
      if (eventName === "BridgeReady") {
        noteUnityBridgeReady();
        return;
      }
      noteUnityBridgeReady();
      if (args.length >= 2 && typeof args[0] === "string") {
        const nestedEventName = args[0];
        const payload = unwrapUnityPayload(args[1]);
        logUnityInbound({ eventName: nestedEventName, jsonData: payload }, `unity event bridge (${nestedEventName})`);
        dispatchUnityBridgeEvent(nestedEventName, payload);
        return;
      }
      const payload = unwrapUnityPayload(args[0]);
      logUnityInbound(payload, `unity event bridge (${eventName})`);
      dispatchUnityBridgeEvent(eventName, payload);
    };

    const unityHandlers = unityEventNames.map((name) => {
      const handler = onUnityResponse(name);
      addEventListener(name, handler);
      return { name, handler };
    });
    return () => unityHandlers.forEach(({ name, handler }) => removeEventListener(name, handler));
  }, [addEventListener, removeEventListener, noteUnityBridgeReady]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const previousOnUnityMessage = window.onUnityMessage;
    window.onUnityMessage = (message) => {
      noteUnityBridgeReady();
      const { event, payload, raw } = parseUnityOutgoingMessage(message);
      logUnityInbound(
        event ? { event, payload } : message,
        event ? `onUnityMessage (${event})` : "onUnityMessage"
      );
      if (event) {
        dispatchUnityBridgeEvent(event, unwrapUnityPayload(payload));
      }
      if (typeof previousOnUnityMessage === "function" && previousOnUnityMessage !== window.onUnityMessage) {
        try {
          previousOnUnityMessage(raw ?? message);
        } catch (e) {
          console.error("previous onUnityMessage handler error", e);
        }
      }
    };

    const configuredHandlerName = process.env.REACT_APP_UNITY_GLOBAL_HANDLER || DEFAULT_UNITY_EVENT;
    const configuredWindowEvent = process.env.REACT_APP_UNITY_WINDOW_EVENT || "unity-message";
    const globalHandlerNames = [configuredHandlerName, ...UNITY_EVENT_FALLBACKS].filter(
      (name, index, arr) => name && arr.indexOf(name) === index
    );
    const windowEventNames = [configuredWindowEvent, ...UNITY_WINDOW_EVENT_FALLBACKS].filter(
      (name, index, arr) => name && arr.indexOf(name) === index
    );

    const customEventListener = (event) =>
      logUnityInbound(event?.detail, `window "${event?.type}" CustomEvent`);
    const directFunctionHandler = (message, ...rest) =>
      logUnityInbound(rest.length > 0 ? { message, extra: rest } : message, "window global function");
    const postMessageListener = (event) => {
      const payload = event?.data;
      if (payload == null) return;

      let parsedPayload = payload;
      if (typeof payload === "string") {
        try {
          parsedPayload = JSON.parse(payload);
        } catch {
          // keep string payload
        }
      }

      if (typeof parsedPayload === "string") {
        if (parsedPayload.toLowerCase().includes("unity")) {
          logUnityInbound(parsedPayload, "window postMessage(string)");
        }
        return;
      }

      const eventName = parsedPayload?.eventName || parsedPayload?.event || parsedPayload?.type;
      const looksLikeUnityPayload =
        parsedPayload?.source === "unity" ||
        parsedPayload?.from === "unity" ||
        parsedPayload?.unity === true ||
        windowEventNames.includes(eventName) ||
        UNITY_EVENT_FALLBACKS.includes(eventName);

      if (!looksLikeUnityPayload) return;
      logUnityInbound(
        parsedPayload?.detail ?? parsedPayload?.data ?? parsedPayload?.message ?? parsedPayload,
        "window postMessage(object)"
      );
    };

    const onUnityEventHandler = (eventName, jsonData) => {
      noteUnityBridgeReady();
      logUnityInbound({ eventName, jsonData }, `window.onUnityEvent(${eventName})`);
    };
    const legacyCallbackHandlers = {
      onUnityHotspot: (hotspotId) => {
        noteUnityBridgeReady();
        logUnityInbound(hotspotId, "window.onUnityHotspot");
        dispatchUnityBridgeEvent("HotspotClicked", hotspotId);
      },
      onUnityRoomEntered: (buildingID, roomID) => {
        noteUnityBridgeReady();
        logUnityInbound({ buildingID, roomID }, "window.onUnityRoomEntered");
        dispatchUnityBridgeEvent("RoomEntered", { buildingID, roomID });
      },
      onUnityBuildingEntered: (buildingID) => {
        noteUnityBridgeReady();
        logUnityInbound(buildingID, "window.onUnityBuildingEntered");
        dispatchUnityBridgeEvent("BuildingEntered", { buildingID });
      },
      onUnityBuildingExited: () => {
        noteUnityBridgeReady();
        logUnityInbound({ event: "BuildingExited" }, "window.onUnityBuildingExited");
        dispatchUnityBridgeEvent("BuildingExited", null);
      },
      onUnityDoorClicked: () => {
        noteUnityBridgeReady();
        logUnityInbound({ event: "DoorClicked" }, "window.onUnityDoorClicked");
        dispatchUnityBridgeEvent("DoorClicked", null);
      },
    };

    const previousGlobalHandlers = {};
    globalHandlerNames.forEach((name) => {
      previousGlobalHandlers[name] = window[name];
      window[name] = directFunctionHandler;
    });
    previousGlobalHandlers.onUnityEvent = window.onUnityEvent;
    window.onUnityEvent = onUnityEventHandler;
    Object.keys(legacyCallbackHandlers).forEach((name) => {
      previousGlobalHandlers[name] = window[name];
      window[name] = legacyCallbackHandlers[name];
    });
    windowEventNames.forEach((name) => window.addEventListener(name, customEventListener));
    window.addEventListener("message", postMessageListener);

    return () => {
      windowEventNames.forEach((name) => window.removeEventListener(name, customEventListener));
      window.removeEventListener("message", postMessageListener);
      globalHandlerNames.forEach((name) => {
        if (previousGlobalHandlers[name] === undefined) {
          delete window[name];
        } else {
          window[name] = previousGlobalHandlers[name];
        }
      });
      if (previousGlobalHandlers.onUnityEvent === undefined) {
        delete window.onUnityEvent;
      } else {
        window.onUnityEvent = previousGlobalHandlers.onUnityEvent;
      }
      Object.keys(legacyCallbackHandlers).forEach((name) => {
        if (previousGlobalHandlers[name] === undefined) {
          delete window[name];
        } else {
          window[name] = previousGlobalHandlers[name];
        }
      });
      if (previousOnUnityMessage === undefined) {
        delete window.onUnityMessage;
      } else {
        window.onUnityMessage = previousOnUnityMessage;
      }
    };
  }, [noteUnityBridgeReady]);

  useEffect(() => {
    installUnityKeyboardGuard();
  }, []);

  const loadPercent = Math.min(100, Math.max(0, Math.round((loadingProgression ?? 0) * 100)));

  return (
    <div
      data-unity-message-panel
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        fontFamily: "'DM Sans', sans-serif",
        background: "#0F172A",
        color: "#E2E8F0",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          flex: fullscreen ? 1 : "0 0 auto",
          borderRadius: fullscreen ? 0 : 8,
          overflow: "hidden",
          border: fullscreen ? "none" : "1px solid rgba(255,255,255,0.1)",
          background: "#111",
        }}
      >
        <Unity
          unityProvider={unityProvider}
          matchWebGLToCanvasSize
          devicePixelRatio={1}
          tabIndex={-1}
          disabledCanvasEvents={["keydown", "keyup", "keypress"]}
          style={{ width: "100%", height: "100%", display: "block", border: "none" }}
        />
        {(!isLoaded || holdReveal) && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              background: "#0F172A",
              zIndex: 5,
            }}
          >
            {initialisationError ? (
              <>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#F87171" }}>Unity failed to load</div>
                <div
                  style={{
                    maxWidth: 420,
                    padding: "0 24px",
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: "rgba(255,255,255,0.65)",
                    textAlign: "center",
                  }}
                >
                  {initialisationError.message || String(initialisationError)}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#E2E8F0" }}>Loading 3D world…</div>
                <div style={{ width: 280, maxWidth: "80vw" }}>
                  <div
                    style={{
                      height: 6,
                      borderRadius: 999,
                      background: "rgba(255,255,255,0.12)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${loadPercent}%`,
                        borderRadius: 999,
                        background: "linear-gradient(90deg, #4DB8B5, #38bdf8)",
                        transition: "width 0.25s ease",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "rgba(255,255,255,0.55)",
                      textAlign: "center",
                    }}
                  >
                    {loadPercent}%
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {isLoaded && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
            }}
          >
            <UnityFpsPanel fps={fps} />
          </div>
        )}
      </div>
    </div>
  );
}
